"use client";

import React, { useCallback, useMemo } from "react";
import { BettingOdds as BettingOddsUI } from "fansunited-sports-ui";
import { useServerMatchOdds } from "../useServerMatchOdds";
import { toMatchOddsMap } from "../adapters";
import type { OddsData, MarketType } from "../types";
import type { FUSportsBettingOperatorOdds } from "fansunited-data-layer";
import { useBetslip } from "@/features/betslip/useBetslip";
import type { BetslipMarket } from "@/features/betslip/types";
import { useOddsDisplay } from "../useOddsDisplay";

export interface BettingOddsStyles {
    container?: string;
    operatorContainer?: string;
    oddsContainer?: string;
    /** Display mode - "flash" cycles through odds */
    mode?: "default" | "flash";
    /** Interval in ms for flash mode cycling */
    flashInterval?: number;
}

interface BettingOddsProps {
    matchId: string;
    markets?: string[];
    operatorId?: string;
    /** Whether the match is live - enables polling every 30 seconds */
    isLive?: boolean;
    /** Additional class name for the container */
    className?: string;
    /** Styles to pass to BettingOddsUI */
    styles?: BettingOddsStyles;
    /** Market type for betslip integration */
    market?: MarketType;
    /** Pre-fetched odds map from a parent batch call. When provided, skips internal fetch. */
    prefetchedOdds?: Map<string, FUSportsBettingOperatorOdds[]>;
    /** ISO 8601 kickoff timestamp — passed to addOddsSelection so persisted items expire 3 h after kickoff */
    kickoffAt?: string;
}

/**
 * BettingOdds component that displays betting odds for a single match
 * Uses the batch odds hook internally for consistency
 *
 * @param matchId - The match ID to fetch odds for
 * @param markets - Array of betting market types (default: ["1x2"])
 * @param operatorId - Optional betting operator ID to filter odds
 * @param isLive - Whether the match is live (enables polling)
 * @param styles - Styles to pass to BettingOddsUI (including mode and flashInterval)
 * @param homeTeam - Home team info for betslip integration
 * @param awayTeam - Away team info for betslip integration
 * @param market - Market type for betslip integration
 */
export const BettingOdds: React.FC<BettingOddsProps> = ({
    matchId,
    markets = ["1x2"],
    operatorId,
    isLive = false,
    className = "",
    styles,
    market,
    prefetchedOdds,
    kickoffAt,
}) => {
    const { addOddsSelection, canAddOdds } = useBetslip();
    const tenantOdds = useOddsDisplay();

    // Skip internal fetch when parent provides pre-fetched odds
    const { oddsMap: rawOddsMap, loading: internalLoading } = useServerMatchOdds({
        matchId: prefetchedOdds ? "" : matchId,
        markets,
        operatorId,
        isLive,
    });

    const loading = prefetchedOdds ? false : internalLoading;
    const oddsMap = useMemo(
        () => (prefetchedOdds ? toMatchOddsMap(prefetchedOdds) : toMatchOddsMap(rawOddsMap)),
        [prefetchedOdds, rawOddsMap]
    );

    // Get odds for this specific match
    const bookmakerOddsList = oddsMap.get(matchId) || [];

    // Check if we should intercept odds clicks
    const shouldInterceptOdds = canAddOdds && !!market;

    // Handle odds click - add to betslip via provider
    const handleOddsClick = useCallback(
        (odd: OddsData) => {
            if (!shouldInterceptOdds || !market) return;
            addOddsSelection(matchId, market as BetslipMarket, odd.label, kickoffAt);
        },
        [shouldInterceptOdds, market, matchId, addOddsSelection, kickoffAt]
    );

    // Map odds with onClick handlers if in predictions-and-odds mode
    const processedBookmakerOddsList = useMemo(() => {
        if (!shouldInterceptOdds) {
            return bookmakerOddsList;
        }

        return bookmakerOddsList.map((bookmakerOdds) => ({
            ...bookmakerOdds,
            odds: bookmakerOdds.odds.map((odd) => ({
                ...odd,
                onClick: () => handleOddsClick(odd),
                // Strip URL to render as button instead of link
                url: undefined,
            })),
        }));
    }, [bookmakerOddsList, shouldInterceptOdds, handleOddsClick]);

    // Show loading skeleton
    if (loading) {
        return (
            <div className="flex gap-2 animate-pulse">
                <div className="flex-1 h-8 bg-muted rounded"></div>
                <div className="flex-1 h-8 bg-muted rounded"></div>
                <div className="flex-1 h-8 bg-muted rounded"></div>
            </div>
        );
    }

    // Show nothing if no odds available
    if (bookmakerOddsList.length === 0) {
        return null;
    }

    // Merge default styles with provided styles. Tenant-resolved mode/interval
    // are defaults; explicit styles props win.
    const mergedStyles = {
        container: styles?.container ?? "p-0",
        operatorContainer: styles?.operatorContainer ?? "p-0",
        oddsContainer: `${styles?.oddsContainer ?? "px-2 py-1.5"} odds-button`,
        mode: styles?.mode ?? tenantOdds.displayMode,
        flashInterval: styles?.flashInterval ?? tenantOdds.flashInterval,
    };

    // Render all bookmakers one under the other
    return (
        <div className={`space-y-3 ${className}`}>
            {processedBookmakerOddsList.map((bookmakerOdds, index) => (
                <BettingOddsUI
                    key={`${bookmakerOdds.operator.name}-${index}`}
                    odds={bookmakerOdds.odds}
                    operator={bookmakerOdds.operator}
                    styles={mergedStyles}
                />
            ))}
        </div>
    );
};
